import { useState } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  AdminTable, AdminModal, ConfirmDialog, FormField,
  StatusBadge, LoadingState, ErrorState, ApiModeBanner, Toggle, inputCls,
} from "../components/AdminUI";
import { useAdminT } from "../../../hooks/useAdminT";
import { useToast } from "../../../components/base/Toast";
import { useAdminData } from "../../../store/adminDataStore";
import {
  LEGAL_DOC_TYPES, LEGAL_I18N_LANGS,
  type LegalDocument, type LegalDocType, type LegalI18nLang,
} from "../../../store/types";

const NOW = () => new Date().toISOString();

const LANG_LABELS: Record<LegalI18nLang, string> = {
  "zh-TW": "中文",
  "en-US": "EN",
  "ja-JP": "日本語",
};

const AUDIENCE_OPTIONS = [
  { value: "booking_customer", label: "預訂客戶 / Booking Customer" },
  { value: "photographer", label: "攝影師 / Photographer" },
];

const REQUIRED_FOR_OPTIONS = [
  { value: "booking", label: "預訂流程 / Booking" },
  { value: "photographer_onboarding", label: "攝影師入職 / Photographer Onboarding" },
];

function emptyI18n(): Record<LegalI18nLang, string> {
  return { "zh-TW": "", "en-US": "", "ja-JP": "" };
}

function emptyDoc(): LegalDocument {
  return {
    id: `legal-${Date.now()}`,
    doc_type: "terms",
    doc_key: "",
    title_i18n: emptyI18n(),
    content_i18n: emptyI18n(),
    version: "1.0",
    effective_date: new Date().toISOString().slice(0, 10),
    is_active: true,
    audience: [],
    required_for: [],
    is_mandatory: true,
    display_order: 99,
    created_at: NOW(),
    updated_at: NOW(),
  };
}

function I18nLangTabs({
  activeLang, onChange,
}: { activeLang: LegalI18nLang; onChange: (lang: LegalI18nLang) => void }) {
  return (
    <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg w-fit">
      {LEGAL_I18N_LANGS.map((lang) => (
        <button key={lang} onClick={() => onChange(lang)}
          className={`px-3 py-1 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${
            activeLang === lang ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"
          }`}>
          {LANG_LABELS[lang]}
        </button>
      ))}
    </div>
  );
}

function docTypeLabel(t: ReturnType<typeof useAdminT>, type: string) {
  const key = `tabifotoAdmin.legal_documents.doc_type_${type}`;
  const label = t(key);
  return label === key ? type : label;
}

// ─── MultiCheckbox helper ──────────────────────────────────────────────────────

function MultiCheckboxField({
  label, options, values, onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  values: string[];
  onChange: (vals: string[]) => void;
}) {
  const toggle = (v: string) => {
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  };
  return (
    <FormField label={label}>
      <div className="flex flex-wrap gap-3 pt-1">
        {options.map((opt) => (
          <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={values.includes(opt.value)}
              onChange={() => toggle(opt.value)}
              className="w-3.5 h-3.5 accent-[#2C2C2C] cursor-pointer" />
            <span className="text-xs text-[#2C2C2C]/70">{opt.label}</span>
          </label>
        ))}
      </div>
    </FormField>
  );
}

// ─── EditModal ────────────────────────────────────────────────────────────────

function EditModal({
  form, onChange, onClose, onSave, saving, isNew,
}: {
  form: LegalDocument; onChange: (d: LegalDocument) => void;
  onClose: () => void; onSave: () => void;
  saving: boolean; isNew: boolean;
}) {
  const t = useAdminT();
  const [activeLang, setActiveLang] = useState<LegalI18nLang>("zh-TW");

  const set = (field: keyof LegalDocument, value: unknown) =>
    onChange({ ...form, [field]: value });

  const setI18n = (field: "title_i18n" | "content_i18n", lang: LegalI18nLang, value: string) =>
    onChange({ ...form, [field]: { ...form[field], [lang]: value } });

  return (
    <AdminModal
      title={isNew ? t("tabifotoAdmin.legal_documents.add_document") : t("tabifotoAdmin.legal_documents.edit_document")}
      onClose={onClose} onSave={onSave} saving={saving}
    >
      {/* Row 1: doc_type + doc_key */}
      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.legal_documents.doc_type")} required>
          <select className={inputCls} value={form.doc_type}
            onChange={(e) => set("doc_type", e.target.value as LegalDocType)}>
            {LEGAL_DOC_TYPES.map((dt) => (
              <option key={dt} value={dt}>{docTypeLabel(t, dt)}</option>
            ))}
          </select>
        </FormField>
        <FormField label={t("tabifotoAdmin.legal_documents.doc_key")}>
          <input className={inputCls} value={form.doc_key}
            onChange={(e) => set("doc_key", e.target.value)}
            placeholder="e.g. terms_v2 (unique system key)" />
        </FormField>
      </div>

      {/* Row 2: version + effective_date */}
      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.legal_documents.version")} required>
          <input className={inputCls} value={form.version}
            onChange={(e) => set("version", e.target.value)} placeholder="1.0" />
        </FormField>
        <FormField label={t("tabifotoAdmin.legal_documents.effective_date")} required>
          <input className={inputCls} type="date" value={form.effective_date}
            onChange={(e) => set("effective_date", e.target.value)} />
        </FormField>
      </div>

      {/* Row 3: audience + required_for */}
      <div className="grid grid-cols-2 gap-4">
        <MultiCheckboxField
          label={t("tabifotoAdmin.legal_documents.audience")}
          options={AUDIENCE_OPTIONS}
          values={form.audience ?? []}
          onChange={(vals) => set("audience", vals)}
        />
        <MultiCheckboxField
          label={t("tabifotoAdmin.legal_documents.required_for")}
          options={REQUIRED_FOR_OPTIONS}
          values={form.required_for ?? []}
          onChange={(vals) => set("required_for", vals)}
        />
      </div>

      {/* Row 4: is_mandatory + display_order */}
      <div className="grid grid-cols-2 gap-4 items-start">
        <FormField label={t("tabifotoAdmin.legal_documents.is_mandatory")}>
          <div className="flex items-center gap-3 pt-1">
            <Toggle checked={form.is_mandatory ?? true} onChange={(v) => set("is_mandatory", v)} />
            <span className="text-xs text-[#2C2C2C]/50">
              {(form.is_mandatory ?? true)
                ? t("tabifotoAdmin.legal_documents.mandatory_yes")
                : t("tabifotoAdmin.legal_documents.mandatory_no")}
            </span>
          </div>
        </FormField>
        <FormField label={t("tabifotoAdmin.legal_documents.display_order")}>
          <input className={inputCls} type="number" min={1} max={999}
            value={form.display_order ?? 99}
            onChange={(e) => set("display_order", parseInt(e.target.value, 10) || 99)} />
        </FormField>
      </div>

      {/* Language tabs */}
      <div className="mb-2">
        <I18nLangTabs activeLang={activeLang} onChange={setActiveLang} />
      </div>

      <FormField label={t("tabifotoAdmin.legal_documents.title_field")} required>
        <input className={inputCls} key={activeLang}
          value={form.title_i18n[activeLang]}
          onChange={(e) => setI18n("title_i18n", activeLang, e.target.value)}
          placeholder={`Title in ${LANG_LABELS[activeLang]}`} />
      </FormField>

      <FormField label={t("tabifotoAdmin.legal_documents.content_field")} required>
        <textarea className={`${inputCls} h-44 resize-none`} key={activeLang}
          value={form.content_i18n[activeLang]}
          onChange={(e) => setI18n("content_i18n", activeLang, e.target.value)}
          placeholder={`Content in ${LANG_LABELS[activeLang]}`}
          maxLength={500} />
        <p className="text-right text-[10px] text-[#2C2C2C]/30 mt-0.5">
          {form.content_i18n[activeLang].length} / 500
        </p>
      </FormField>

      <div className="flex items-center gap-3 mt-1">
        <Toggle checked={form.is_active} onChange={(v) => set("is_active", v)} />
        <span className="text-xs text-[#2C2C2C]/50">
          {form.is_active ? t("tabifotoAdmin.common.active") : t("tabifotoAdmin.common.inactive")}
        </span>
      </div>
    </AdminModal>
  );
}

// ─── RequiredForBadges helper ─────────────────────────────────────────────────

function RequiredForBadges({ values, isMandatory }: { values: string[]; isMandatory: boolean }) {
  if (!values || values.length === 0) return <span className="text-xs text-[#2C2C2C]/20">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {values.map((v) => {
        const isBooking = v === "booking";
        return (
          <span key={v}
            className={`px-1.5 py-0.5 rounded text-[10px] font-light ${
              isBooking ? "bg-amber-50 text-amber-700" : "bg-teal-50 text-teal-700"
            }`}>
            {isBooking ? "預訂" : "攝影師"}
          </span>
        );
      })}
      {isMandatory && (
        <span className="px-1.5 py-0.5 rounded text-[10px] font-light bg-rose-50 text-rose-600">必填</span>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdminLegalDocumentsPage() {
  const t = useAdminT();
  const { state, status, isApiMode, upsertLegalDoc, removeLegalDoc, refetchLegalDocs } = useAdminData();
  const toast = useToast();

  const [editItem, setEditItem] = useState<LegalDocument | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<"all" | LegalDocType>("all");

  const filtered = [...state.legalDocuments]
    .filter((d) => filterType === "all" || d.doc_type === filterType)
    .sort((a, b) => (a.display_order ?? 99) - (b.display_order ?? 99));

  const handleSave = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      await upsertLegalDoc(editItem);
      toast.showToast(t("tabifotoAdmin.common.save_success"), "success");
      setEditItem(null);
    } catch {
      toast.showToast(t("tabifotoAdmin.common.save_error"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await removeLegalDoc(deleteId);
      toast.showToast(t("tabifotoAdmin.common.delete_success"), "success");
    } catch {
      toast.showToast(t("tabifotoAdmin.common.delete_error"), "error");
    } finally {
      setDeleteId(null);
    }
  };

  const FILTER_TABS = [
    { key: "all" as const, label: t("tabifotoAdmin.legal_documents.filter_all") },
    ...LEGAL_DOC_TYPES.map((dt) => ({ key: dt, label: docTypeLabel(t, dt) })),
  ];

  const headers = [
    t("tabifotoAdmin.legal_documents.col_doc_type"),
    t("tabifotoAdmin.legal_documents.col_doc_key"),
    t("tabifotoAdmin.legal_documents.col_title"),
    t("tabifotoAdmin.legal_documents.col_version"),
    t("tabifotoAdmin.legal_documents.col_effective_date"),
    t("tabifotoAdmin.legal_documents.col_required_for"),
    t("tabifotoAdmin.legal_documents.col_active"),
    t("tabifotoAdmin.legal_documents.col_actions"),
  ];

  return (
    <AdminLayout
      title={t("tabifotoAdmin.legal_documents.title")}
      subtitle={t("tabifotoAdmin.legal_documents.subtitle")}
      action={
        <button onClick={() => setEditItem(emptyDoc())}
          className="flex items-center gap-2 px-4 py-2 bg-[#2C2C2C] text-white text-sm rounded-md hover:bg-[#2C2C2C]/80 transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base" /></div>
          {t("tabifotoAdmin.legal_documents.add_document")}
        </button>
      }
    >
      <ApiModeBanner isApiMode={isApiMode} />

      <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg w-fit mb-5 flex-wrap">
        {FILTER_TABS.map((tab) => (
          <button key={tab.key} onClick={() => setFilterType(tab.key)}
            className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${
              filterType === tab.key ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {status.legalDocs.loading ? (
        <LoadingState />
      ) : status.legalDocs.error ? (
        <ErrorState message={status.legalDocs.error} onRetry={refetchLegalDocs} />
      ) : (
        <AdminTable headers={headers} empty={filtered.length === 0}
          emptyMessage={t("tabifotoAdmin.legal_documents.no_documents")}>
          {filtered.map((doc) => (
            <tr key={doc.id}
              className="border-b border-[#D9D9D9]/40 hover:bg-[#F7F6F3]/60 transition-colors">
              <td className="px-5 py-3">
                <span className="px-2 py-0.5 bg-[#F7F6F3] text-[#2C2C2C]/60 rounded text-xs font-mono">
                  {doc.doc_type}
                </span>
              </td>
              <td className="px-5 py-3">
                {doc.doc_key ? (
                  <span className="text-xs font-mono text-[#C8B8A6]">{doc.doc_key}</span>
                ) : (
                  <span className="text-xs text-[#2C2C2C]/20">—</span>
                )}
              </td>
              <td className="px-5 py-3">
                <div>
                  <p className="text-sm font-light text-[#2C2C2C]">
                    {doc.title_i18n["zh-TW"] || doc.title_i18n["en-US"]}
                  </p>
                  {doc.title_i18n["en-US"] && doc.title_i18n["zh-TW"] !== doc.title_i18n["en-US"] && (
                    <p className="text-xs text-[#2C2C2C]/40 mt-0.5">{doc.title_i18n["en-US"]}</p>
                  )}
                </div>
              </td>
              <td className="px-5 py-3">
                <span className="text-xs font-mono text-[#2C2C2C]/60">v{doc.version}</span>
              </td>
              <td className="px-5 py-3 text-xs text-[#2C2C2C]/50">{doc.effective_date}</td>
              <td className="px-5 py-3">
                <RequiredForBadges values={doc.required_for ?? []} isMandatory={doc.is_mandatory ?? false} />
              </td>
              <td className="px-5 py-3"><StatusBadge active={doc.is_active} /></td>
              <td className="px-5 py-3">
                <div className="flex items-center gap-2">
                  <button onClick={() => setEditItem({ ...doc })}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-[#2C2C2C] hover:bg-[#F7F6F3] transition-colors cursor-pointer">
                    <i className="ri-edit-line text-base" />
                  </button>
                  <button onClick={() => setDeleteId(doc.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer">
                    <i className="ri-delete-bin-line text-base" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </AdminTable>
      )}

      {editItem && (
        <EditModal form={editItem} onChange={setEditItem}
          onClose={() => setEditItem(null)} onSave={handleSave}
          saving={saving} isNew={!state.legalDocuments.some((d) => d.id === editItem.id)} />
      )}

      {deleteId && (
        <ConfirmDialog title={t("tabifotoAdmin.legal_documents.delete_confirm")}
          message={t("tabifotoAdmin.common.cannot_undone")}
          danger onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
      )}
    </AdminLayout>
  );
}